// Complete Updated src/pages/RequestsPortal.js
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import "./RequestsPortal.css";

function RequestsPortal() {
  const { user, token } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminResponse, setAdminResponse] = useState("");

  // Get API base URL from environment variables
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  
  const [newRequest, setNewRequest] = useState({
    description: "",
    category: "",
    image: null,
    imagePreview: ""
  });

  const [filter, setFilter] = useState({
    status: "",
    category: "",
    dateFrom: "",
    dateTo: ""
  });

  // Load requests on component mount
  useEffect(() => {
    if (user) {
      fetchRequests();
    }
  }, [user]);

  // Updated fetchRequests function with proper user ID handling
  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      let endpoint;
      
      if (user.role === 'user' || user.role === 'resident') {
        // Use the actual user ID from the logged-in user context
        const userId = user.id || user.user_id || 4; // Fallback to 4 if no ID found
        endpoint = `${API_BASE_URL}/api/requests/my?user_id=${userId}`;
      } else if (user.role === 'admin' || user.role === 'officer' || user.role === 'accounts') {
        endpoint = `${API_BASE_URL}/api/requests/all`;
      } else {
        const userId = user.id || user.user_id || 4;
        endpoint = `${API_BASE_URL}/api/requests/my?user_id=${userId}`;
      }

      console.log('Fetching from:', endpoint);
      console.log('User context:', user);

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        if (response.status === 404 || response.status === 500) {
          setRequests([]);
          return;
        }
        throw new Error(`Failed to fetch requests: ${response.status}`);
      }

      const data = await response.json();
      console.log('Requests data:', data);
      
      // Handle the response format
      if (data.success && Array.isArray(data.data)) {
        setRequests(data.data);
      } else if (Array.isArray(data)) {
        setRequests(data);
      } else {
        setRequests([]);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      
      if (err.message.includes('fetch') || err.name === 'TypeError') {
        setError('Unable to connect to server. Please check if the server is running.');
      } else if (err.message.includes('500')) {
        setRequests([]);
        setError(null);
      } else {
        setError(`Failed to load requests: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }

      setNewRequest({
        ...newRequest,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newRequest.description.trim() || !newRequest.category) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('description', newRequest.description.trim());
      formData.append('category', newRequest.category);
      formData.append('status', 'pending');
      
      // Use the actual user ID and name from the logged-in user context
      const userId = user.id || user.user_id || 4;
      formData.append('user_id', userId.toString());
      formData.append('resident_id', userId.toString());
      formData.append('resident_name', user.name || user.username || 'Unknown User');
      formData.append('resident_pno', user.pno || user.employee_id || '');
      formData.append('resident_phone', user.phone || '');
      
      if (newRequest.image) {
        formData.append('image', newRequest.image);
      }

      console.log('Submitting request with user data:', {
        userId,
        name: user.name || user.username,
        pno: user.pno || user.employee_id,
        phone: user.phone
      });

      const response = await fetch(`${API_BASE_URL}/api/requests`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to submit request: ${errorData}`);
      }

      const result = await response.json();
      console.log('Request submitted successfully:', result);

      // Reset form
      setNewRequest({
        description: "",
        category: "",
        image: null,
        imagePreview: ""
      });

      // Refresh requests list
      await fetchRequests();
      
      alert('Request submitted successfully!');

    } catch (err) {
      console.error('Error submitting request:', err);
      alert(`Failed to submit request: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const openResolveModal = (request) => {
    setSelectedRequest(request);
    setAdminResponse("");
    setShowResolveModal(true);
  };

  const closeResolveModal = () => {
    setShowResolveModal(false);
    setSelectedRequest(null);
    setAdminResponse("");
  };

  const handleResolveRequest = async () => {
    if (!selectedRequest || user.role !== 'admin') {
      alert('Only administrators can resolve requests');
      return;
    }

    if (!adminResponse.trim()) {
      alert('Please provide a response message for the resident');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${selectedRequest.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'resolved',
          admin_response: adminResponse.trim(),
          updated_by: user.id || user.user_id || 1 // Use actual admin user ID
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to resolve request');
      }

      // Refresh requests
      await fetchRequests();
      closeResolveModal();
      alert('Request resolved successfully with admin response!');

    } catch (err) {
      console.error('Error resolving request:', err);
      alert('Failed to resolve request');
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    if (user.role !== 'admin') {
      alert('Only administrators can update request status');
      return;
    }

    if (!window.confirm(`Are you sure you want to mark this request as ${newStatus}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${requestId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status: newStatus,
          updated_by: user.id || user.user_id || 1 // Use actual admin user ID
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update request status');
      }

      // Refresh requests
      await fetchRequests();
      alert(`Request marked as ${newStatus} successfully!`);

    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update request status');
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  // Filter requests based on filter criteria
  const filteredRequests = requests.filter(request => {
    if (filter.status && request.status !== filter.status) return false;
    if (filter.category && request.category !== filter.category) return false;
    if (filter.dateFrom && new Date(request.created_at) < new Date(filter.dateFrom)) return false;
    if (filter.dateTo && new Date(request.created_at) > new Date(filter.dateTo)) return false;
    return true;
  });

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'fas fa-clock';
      case 'in-progress': return 'fas fa-spinner';
      case 'resolved': return 'fas fa-check-circle';
      case 'rejected': return 'fas fa-times-circle';
      default: return 'fas fa-clipboard';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'water': return 'fas fa-tint';
      case 'electricity': return 'fas fa-bolt';
      case 'repair': return 'fas fa-wrench';
      case 'sanitation': return 'fas fa-broom';
      default: return 'fas fa-file-alt';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'user': return 'Resident';
      case 'resident': return 'Resident';
      case 'admin': return 'Administrator';
      case 'officer': return 'Officer';
      case 'accounts': return 'Accounts';
      default: return role;
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isResident = user?.role === 'user' || user?.role === 'resident';
  const canUpdateStatus = user?.role === 'admin';
  const canViewAllRequests = user?.role === 'admin' || user?.role === 'officer' || user?.role === 'accounts';

  if (loading) {
    return (
      <div className="requests-portal">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="requests-portal">
        <div className="error-message">
          <h2><i className="fas fa-exclamation-triangle"></i> Error Loading Data</h2>
          <p>{error}</p>
          <button onClick={fetchRequests}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="requests-portal">
      <button className="back-button" onClick={handleBack}>
        <i className="fas fa-arrow-left"></i> Back to Dashboard
      </button>

      <div className="portal-header">
        <h1><i className="fas fa-clipboard-list"></i> Requests Portal</h1>
        <div className="user-info">
          <p>Role: <span className={`role-badge ${user.role}`}>{getRoleDisplayName(user.role)}</span></p>
          <p>User: {user.name || user.username}</p>
          {user.phone && <p>Phone: {user.phone}</p>}
        </div>
      </div>

      {/* Filter Section - for Admin, Officer, and Accounts */}
      {canViewAllRequests && (
        <div className="filter-section card">
          <h3><i className="fas fa-filter"></i> Filter Requests</h3>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Status</label>
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Category</label>
              <select
                value={filter.category}
                onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              >
                <option value="">All Categories</option>
                <option value="Water">Water Issue</option>
                <option value="Electricity">Electricity Issue</option>
                <option value="Repair">Repair Work</option>
                <option value="Sanitation">Sanitation</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="filter-group">
              <label>From Date</label>
              <input
                type="date"
                value={filter.dateFrom}
                onChange={(e) => setFilter({ ...filter, dateFrom: e.target.value })}
              />
            </div>
            <div className="filter-group">
              <label>To Date</label>
              <input
                type="date"
                value={filter.dateTo}
                onChange={(e) => setFilter({ ...filter, dateTo: e.target.value })}
              />
            </div>
            <button 
              className="btn-secondary"
              onClick={() => setFilter({ status: "", category: "", dateFrom: "", dateTo: "" })}
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Requests List */}
      <div className="request-list card">
        <div className="list-header">
          <h2>
            <i className={isResident ? "fas fa-user" : "fas fa-list"}></i>
            {isResident ? ' My Requests' : ' All Requests'}
          </h2>
          <div className="request-stats">
            <span>Total: {filteredRequests.length}</span>
            <span>Pending: {filteredRequests.filter(r => r.status === 'pending').length}</span>
            <span>Resolved: {filteredRequests.filter(r => r.status === 'resolved').length}</span>
          </div>
        </div>

        {filteredRequests.length > 0 ? (
          <div className="requests-grid">
            {filteredRequests.map((request) => (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <div className="request-id">
                    <span className="request-category">
                      <i className={getCategoryIcon(request.category)}></i> {request.category}
                    </span>
                  </div>
                  <span className={`status-badge status-${request.status?.toLowerCase()?.replace(" ", "-")}`}>
                    <i className={getStatusIcon(request.status)}></i> {request.status}
                  </span>
                </div>

                {/* Resident Info - only for Admin, Officer, and Accounts */}
                {canViewAllRequests && (
                  <div className="resident-info">
                    <div className="resident-details">
                      <p><strong><i className="fas fa-user"></i> Resident:</strong> {request.resident_name || request.quarter_resident_name || request.user_name || user.name || user.username || 'N/A'}</p>
                      <p><strong><i className="fas fa-id-badge"></i> PNO:</strong> {request.resident_pno || request.quarter_pno || user.pno || user.employee_id || 'N/A'}</p>
                      <p><strong><i className="fas fa-phone"></i> Phone:</strong> {request.resident_phone || request.quarter_phone || request.user_phone || user.phone || 'N/A'}</p>
                      <p><strong><i className="fas fa-hashtag"></i> ID:</strong> #{request.id}</p>
                    </div>
                  </div>
                )}

                <div className="request-body">
                  <p className="request-description">{request.description}</p>
                  
                  {/* Image Display with Better Error Handling */}
                  {request.image_url && (
                    <div className="request-image">
                      <img 
                        src={request.image_url.startsWith('http') ? request.image_url : `${API_BASE_URL}${request.image_url}`}
                        alt="Request evidence"
                        onError={(e) => {
                          console.error('Failed to load image:', request.image_url);
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200"%3E%3Crect width="300" height="200" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial" font-size="14"%3EImage unavailable%3C/text%3E%3C/svg%3E';
                        }}
                        crossOrigin="anonymous"
                      />
                      <a 
                        href={request.image_url.startsWith('http') ? request.image_url : `${API_BASE_URL}${request.image_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-image-link"
                      >
                        <i className="fas fa-external-link-alt"></i> View Full Image
                      </a>
                    </div>
                  )}

                  {/* Admin Response Section - Always show if there's a response */}
                  {request.admin_response && (
                    <div className="admin-response">
                      <div className="admin-response-header">
                        <h4><i className="fas fa-reply"></i> Administration Response</h4>
                        <span className="response-status resolved">
                          <i className="fas fa-check-circle"></i> Response Provided
                        </span>
                      </div>
                      <div className="admin-response-content">
                        <p>{request.admin_response}</p>
                        {request.resolved_by && (
                          <div className="resolved-by">
                            <small><i className="fas fa-user-shield"></i> Resolved by: {request.resolved_by}</small>
                          </div>
                        )}
                        {request.resolved_at && (
                          <div className="response-date">
                            <small><i className="fas fa-calendar"></i> Resolved on: {formatDateTime(request.resolved_at)}</small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="request-footer">
                  <div className="request-meta">
                    <span className="request-date">
                      <i className="fas fa-calendar"></i> Submitted: {formatDateTime(request.created_at)}
                    </span>
                    {request.updated_at !== request.created_at && !request.admin_response && (
                      <span className="request-updated">
                        <i className="fas fa-sync"></i> Updated: {formatDateTime(request.updated_at)}
                      </span>
                    )}
                  </div>

                  {/* Admin Actions */}
                  {canUpdateStatus && request.status !== 'resolved' && request.status !== 'rejected' && (
                    <div className="admin-actions">
                      <button
                        className="btn-success"
                        onClick={() => openResolveModal(request)}
                      >
                        <i className="fas fa-check"></i> Resolve with Response
                      </button>
                      <button
                        className="btn-warning"
                        onClick={() => handleStatusUpdate(request.id, 'in-progress')}
                      >
                        <i className="fas fa-spinner"></i> Mark In Progress
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleStatusUpdate(request.id, 'rejected')}
                      >
                        <i className="fas fa-times"></i> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-requests">
            <div className="no-requests-content">
              <div className="no-requests-icon"><i className="fas fa-inbox"></i></div>
              <h3>No Requests Found</h3>
              <p>
                {isResident 
                  ? "You haven't submitted any requests yet. Use the form below to raise your first request." 
                  : "No requests found matching the current filters. Try adjusting your filter criteria."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Raise New Request - only for Residents */}
      {isResident && (
        <div className="new-request card">
          <h2><i className="fas fa-plus"></i> Raise a New Request</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                placeholder="Describe your request in detail..."
                value={newRequest.description}
                onChange={(e) =>
                  setNewRequest({ ...newRequest, description: e.target.value })
                }
                rows="4"
                maxLength="1000"
                required
              />
              <small>{newRequest.description.length}/1000 characters</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                value={newRequest.category}
                onChange={(e) =>
                  setNewRequest({ ...newRequest, category: e.target.value })
                }
                required
              >
                <option value="">Select Category</option>
                <option value="Water"><i className="fas fa-tint"></i> Water Issue</option>
                <option value="Electricity"><i className="fas fa-bolt"></i> Electricity Issue</option>
                <option value="Repair"><i className="fas fa-wrench"></i> Repair Work</option>
                <option value="Sanitation"><i className="fas fa-broom"></i> Sanitation</option>
                <option value="Other"><i className="fas fa-file-alt"></i> Other</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="file">Upload Image (Optional)</label>
              <input
                id="file"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              <small>Max file size: 5MB. Supported formats: JPG, PNG, GIF</small>
              
              {newRequest.imagePreview && (
                <div className="image-preview">
                  <img src={newRequest.imagePreview} alt="Preview" />
                  <button 
                    type="button"
                    onClick={() => setNewRequest({
                      ...newRequest, 
                      image: null, 
                      imagePreview: ""
                    })}
                  >
                    <i className="fas fa-times"></i> Remove
                  </button>
                </div>
              )}
            </div>
            
            <button 
              type="submit" 
              disabled={submitting || !newRequest.description.trim() || !newRequest.category}
              className={submitting ? 'submitting' : ''}
            >
              {submitting ? <><i className="fas fa-spinner fa-spin"></i> Submitting...</> : <><i className="fas fa-paper-plane"></i> Submit Request</>}
            </button>
          </form>
        </div>
      )}

      {/* Admin Response Modal */}
      {showResolveModal && selectedRequest && (
        <div className="modal-overlay">
          <div className="resolve-modal">
            <div className="modal-header">
              <h3><i className="fas fa-check"></i> Resolve Request #{selectedRequest.id}</h3>
              <button className="close-btn" onClick={closeResolveModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-content">
              <div className="request-summary">
                <h4>Request Details:</h4>
                <p><strong>Category:</strong> {selectedRequest.category}</p>
                <p><strong>Description:</strong> {selectedRequest.description}</p>
                <p><strong>Resident:</strong> {selectedRequest.resident_name || selectedRequest.quarter_resident_name || selectedRequest.user_name || user.name || user.username}</p>
                <p><strong>PNO:</strong> {selectedRequest.resident_pno || selectedRequest.quarter_pno || user.pno || user.employee_id || 'N/A'}</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="adminResponse">
                  Response Message to Resident: <span className="required">*</span>
                </label>
                <textarea
                  id="adminResponse"
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Enter your response to the resident. This message will be visible to them when they view their request status..."
                  rows={5}
                  maxLength="1000"
                  required
                />
                <small>{adminResponse.length}/1000 characters</small>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn-resolve"
                  onClick={handleResolveRequest}
                  disabled={!adminResponse.trim()}
                >
                  <i className="fas fa-check"></i> Resolve with Response
                </button>
                <button className="btn-cancel" onClick={closeResolveModal}>
                  <i className="fas fa-times"></i> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RequestsPortal;